//#region src/utils/csv-file-picker.ts
function openCsvFilePicker(onFile, options = {}) {
	const { accept, multiple = false } = options;
	if (typeof document === "undefined") throw new Error("File picker is not supported in this environment (no DOM available)");
	const input = document.createElement("input");
	input.type = "file";
	input.style.display = "none";
	input.multiple = multiple;
	const defaultAccept = ".csv,text/csv,application/csv";
	input.accept = accept ? `${defaultAccept},${accept}` : defaultAccept;
	input.onchange = function(event) {
		const files = event.target.files;
		try {
			if (!files || files.length === 0) throw new Error("No files were selected");
			const fileArray = Array.from(files);
			if (!fileArray.some((file) => file.type === "text/csv" || file.type === "application/csv" || file.name.toLowerCase().endsWith(".csv")) && !accept) throw new Error("Please select at least one CSV file");
			if (multiple) onFile(fileArray);
			else {
				const file = fileArray[0];
				if (!accept && !file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv" && file.type !== "application/csv") throw new Error("Please select a CSV file");
				onFile(file);
			}
		} catch (error) {
			console.error("CSV file picker error:", error);
			throw error;
		} finally {
			if (input.parentNode) input.parentNode.removeChild(input);
		}
	};
	input.oncancel = function() {
		if (input.parentNode) input.parentNode.removeChild(input);
		throw new Error("File selection was cancelled by the user");
	};
	document.body.appendChild(input);
	input.click();
}
/**
* Reads a CSV file and returns its content as a string.
* 
* @param file - The CSV file to read
* @returns Promise resolving to the file content as a string
* 
* @throws {Error} When file reading fails
* @throws {Error} When file is empty
* 
* @example
* ```typescript
* import { openCsvFilePicker, readCsvFile } from '@therms/react-modules'
* 
* openCsvFilePicker(async (file) => {
*   try {
*     const csvContent = await readCsvFile(file)
*     console.log('CSV content:', csvContent)
*   } catch (error) {
*     console.error('Failed to read CSV file:', error.message)
*   }
* })
* ```
*/
function readCsvFile(file) {
	return new Promise((resolve, reject) => {
		if (!file) {
			reject(/* @__PURE__ */ new Error("No file provided"));
			return;
		}
		if (file.size === 0) {
			reject(/* @__PURE__ */ new Error("The selected file is empty"));
			return;
		}
		const reader = new FileReader();
		reader.onload = function(event) {
			const content = event.target?.result;
			if (typeof content !== "string") {
				reject(/* @__PURE__ */ new Error("Failed to read file as text"));
				return;
			}
			if (!content.trim()) {
				reject(/* @__PURE__ */ new Error("The CSV file appears to be empty"));
				return;
			}
			resolve(content);
		};
		reader.onerror = function() {
			reject(/* @__PURE__ */ new Error("Failed to read the CSV file. Please try again."));
		};
		reader.onabort = function() {
			reject(/* @__PURE__ */ new Error("File reading was aborted"));
		};
		reader.readAsText(file, "UTF-8");
	});
}
//#endregion
export { openCsvFilePicker, readCsvFile };

//# sourceMappingURL=csv-file-picker.js.map